import React, { useState } from "react";
import {
  ActionDialog,
  Button,
  DateField,
  formatDisplayDate,
  parseDisplayDate,
} from "../../../components/ui";
import { formatPrice } from "../../brand/planFormat";
import { toIsoDate } from "../../clients/clientPayload";
import { normaliseDobInput } from "../../clients/validation";

/** Midnight, so two dates compare on the day rather than on the moment. */
const atMidnight = (date: Date): Date => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

export interface ConfirmPaymentModalProps {
  /** What is being marked in. */
  amount: number;
  /** Who it was asked of: "Priya Sharma". */
  payer: string;
  /** What it was for: the plan name. */
  planName: string;
  /** How long that plan runs: "6 Months". */
  duration: string;
  /**
   * ISO datetime the request was raised. The money cannot have arrived before
   * it was asked for, so this is the earliest day the field will take.
   */
  requestedAt: string;
  /** Set while the confirm is with the server. */
  busy?: boolean;
  onClose: () => void;
  /** Confirms it, with the day the money arrived as an ISO date. */
  onConfirm: (receivedOn: string) => void;
}

/**
 * The "money is in" question, asked the same way wherever it is asked — from a
 * client's payment history, and from the enquiry a request was raised against.
 *
 * It asks for a date rather than assuming today, because the two are routinely
 * different: a transfer made on Friday is marked off on Monday, and a payment
 * recorded on the wrong day is a payment the practice's own figures then
 * disagree about. The field opens empty for the same reason — a date nobody
 * chose is a date nobody checked, so today is offered by the calendar rather
 * than filled in on their behalf.
 *
 * The date is bounded at both ends. Nothing can be received before it was
 * asked for, and nothing can be received tomorrow — both are typos rather than
 * intentions, and either would be stored as fact.
 *
 * A sheet up from the bottom edge on a phone rather than a centred panel — see
 * `ActionDialog`. It is a question with a field in it and two answers under
 * that, which is exactly what a phone wants under the thumb.
 */
const ConfirmPaymentModal: React.FC<ConfirmPaymentModalProps> = ({
  amount,
  payer,
  planName,
  duration,
  requestedAt,
  busy = false,
  onClose,
  onConfirm,
}) => {
  const today = atMidnight(new Date());
  const raised = atMidnight(new Date(requestedAt));
  // A request raised today, or one whose date the backend sent in a shape this
  // could not read, leaves today as the only day on offer rather than a range
  // running from `Invalid Date`.
  const earliest =
    Number.isNaN(raised.getTime()) || raised > today ? today : raised;

  const [value, setValue] = useState("");
  // Only shown once the field has been left or the confirm attempted: a date
  // half-typed is not yet wrong.
  const [touched, setTouched] = useState(false);

  const picked = parseDisplayDate(value);

  const error = (): string | undefined => {
    if (!picked) return "Enter the date the payment came in as DD/MM/YYYY.";
    if (picked > today) return "A payment cannot have been received in the future.";
    if (picked < earliest)
      return `This was only requested on ${formatDisplayDate(earliest)}.`;
    return undefined;
  };

  const problem = error();

  const handleConfirm = () => {
    setTouched(true);
    if (problem || busy) return;
    onConfirm(toIsoDate(value));
  };

  return (
    <ActionDialog
      title="Confirm payment"
      onClose={() => !busy && onClose()}
      className="max-w-[440px]"
      // Not while the confirm is with the server: it is going through either
      // way, and a panel dismissed over it would leave nothing on screen to
      // say so.
      dismissable={!busy}
      footer={
        <>
          <Button
            variant="secondary"
            size="md"
            disabled={busy}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button size="md" loading={busy} onClick={handleConfirm}>
            Confirm payment
          </Button>
        </>
      }
    >
      <p className="text-[14px] 3xl:text-[15px] text-ink-muted">
        Mark the <span className="font-medium text-ink">{formatPrice(amount)}</span>{" "}
        requested from <span className="font-medium text-ink">{payer}</span> for{" "}
        {planName} ({duration}) as received? This cannot be undone.
      </p>

      <DateField
        className="mt-4"
        label="Payment received on"
        required
        value={value}
        normalise={normaliseDobInput}
        onValueChange={setValue}
        onBlur={() => setTouched(true)}
        error={touched ? problem : undefined}
        disabled={busy}
        minDate={earliest}
        maxDate={today}
      />
    </ActionDialog>
  );
};

export default ConfirmPaymentModal;
